import { useState, useCallback } from 'react'
import { motion } from 'motion/react'
import { Sun, Sparkles, Calendar, Mail, Shield, BookOpenText, Bell, Brain, AlertCircle, RefreshCw, Play, CloudSun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import type { ScreenName, DeepLink } from '../../shared/types/navigation'
import type { BriefingConfig, BriefingSource } from '../../shared/types/briefing'
import { getBriefingConfig, updateBriefingSetting } from './api'
import { useExpandable } from '../../shared/hooks/useExpandable'
import { useAsyncData } from '../../shared/hooks/useAsyncData'
import { SkeletonCard } from '@tendbot/ui'
import { ToggleSwitch, BackButton, SectionHeading } from '@tendbot/ui'
import { TimeCard, LastBriefingCard } from './components'
import { defaultStagger } from '@tendbot/ui'

const SOURCE_ICONS: Record<string, LucideIcon> = { calendar: Calendar, email: Mail, approvals: Shield, followups: Bell, notebook: BookOpenText, weather: CloudSun }

interface Props {
  onBack: () => void
  onNavigate?: (screen: ScreenName, deepLink?: DeepLink) => void
}

export default function Briefing({ onBack, onNavigate }: Props) {
  const { t } = useTranslation('briefing')
  const { t: tc } = useTranslation('common')
  const fetchBriefing = useCallback(() => getBriefingConfig(), [])
  const { data: fetchedData, loading, error, refetch } = useAsyncData(fetchBriefing, [])
  const [data, setData] = useState<BriefingConfig | null>(null)
  const [prevFetchedData, setPrevFetchedData] = useState<BriefingConfig | null>(null)
  if (fetchedData && fetchedData !== prevFetchedData) {
    setPrevFetchedData(fetchedData)
    setData(fetchedData)
  }
  const expand = useExpandable()

  const { container: containerVariants, item: itemVariants } = defaultStagger

  const updateSetting = async (key: string, value: unknown): Promise<void> => {
    await updateBriefingSetting(key, value)
  }

  const handleMasterToggle = (): void => {
    const newVal = !data!.enabled
    setData(prev => prev ? { ...prev, enabled: newVal } : prev)
    updateSetting('enabled', newVal)
  }

  const handleTimeChange = (time: string): void => {
    setData(prev => prev ? { ...prev, time } : prev)
    updateSetting('time', time)
  }

  const handleWeekdaysToggle = (): void => {
    const newVal = !data!.weekdaysOnly
    setData(prev => prev ? { ...prev, weekdaysOnly: newVal } : prev)
    updateSetting('weekdaysOnly', newVal)
  }

  // ─── Trigger: navigate to chat with a user message ──────
  const handleTrigger = (): void => {
    onNavigate?.('chat', { type: 'send-message', id: 'briefing', message: t('triggerMessage') })
  }

  const handleSourceToggle = (sourceKey: string): void => {
    setData(prev => {
      if (!prev) return prev
      const source = prev.sources[sourceKey]
      if (!source) return prev
      return {
        ...prev,
        sources: {
          ...prev.sources,
          [sourceKey]: { ...source, enabled: !source.enabled }
        }
      }
    })
    const source = data?.sources[sourceKey]
    if (source) {
      updateSetting(`sources.${sourceKey}.enabled`, !source.enabled)
    }
  }

  if (loading) {
    return (
      <div className="px-5 sm:px-8 lg:px-12 py-4 space-y-6">
        <div className="space-y-1">
          <div className="h-4 w-24 bg-charcoal/50 rounded animate-shimmer" />
          <div className="h-8 w-48 bg-charcoal/50 rounded animate-shimmer" />
        </div>
        <SkeletonCard className="h-24" />
        <div className="space-y-3">
          <SkeletonCard className="h-20" />
          <SkeletonCard className="h-32" />
          <SkeletonCard className="h-20" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-5 sm:px-8 lg:px-12 py-4 space-y-6">
        <BackButton onClick={onBack} className="mb-2" />
        <div className="glass-card rounded-xl p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center mx-auto">
            <AlertCircle size={24} className="text-rose" />
          </div>
          <p className="text-pearl/70 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
          >
            <RefreshCw size={14} />
            {tc('actions.retry')}
          </button>
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="px-5 sm:px-8 lg:px-12 py-4 space-y-6 overflow-y-auto h-full"
    >
      <div className="max-w-2xl mx-auto space-y-6">
      {/* Back row */}
      <BackButton onClick={onBack} className="mb-2" />

      {/* Header */}
      <motion.header variants={itemVariants} className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-gold/60">
            <Sun size={16} />
            <Sparkles size={14} />
            <span className="text-xs font-medium uppercase tracking-wider">{t('header')}</span>
          </div>
          <h1 className="font-display text-3xl text-ivory font-semibold">
            {t('title')}
          </h1>
        </div>

        {/* Master toggle */}
        <ToggleSwitch
          checked={data.enabled}
          onChange={handleMasterToggle}
          size="lg"
          variant="bordered"
        />
      </motion.header>

      {/* Generate Now button — always visible */}
      <motion.div variants={itemVariants}>
        <button
          onClick={handleTrigger}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20"
        >
          <Play size={16} />
          {t('triggerButton.idle')}
        </button>
      </motion.div>

      {/* Content wrapper -- fades when disabled */}
      <motion.div
        animate={{ opacity: data.enabled ? 1 : 0.4 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
        style={{ pointerEvents: data.enabled ? 'auto' : 'none' }}
      >
        {/* Time Card */}
        <motion.div variants={itemVariants}>
          <TimeCard
            time={data.time}
            weekdaysOnly={data.weekdaysOnly}
            nextRun={data.nextRun}
            isExpanded={expand.isExpanded('time')}
            onToggleExpand={() => expand.toggle('time')}
            onTimeChange={handleTimeChange}
            onWeekdaysToggle={handleWeekdaysToggle}
          />
        </motion.div>

        {/* Content Sources */}
        <motion.div variants={itemVariants} className="space-y-3">
          <SectionHeading className="font-medium">{t('whatToInclude')}</SectionHeading>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(data.sources).map(([key, source]: [string, BriefingSource]) => {
              const Icon: LucideIcon = SOURCE_ICONS[key] || Brain
              return (
                <div
                  key={key}
                  className={`glass-card rounded-xl p-4 transition-all duration-300 ${
                    source.enabled ? '' : 'opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      source.enabled ? 'bg-gold/20 text-gold' : 'bg-charcoal text-pearl/60'
                    }`}>
                      <Icon size={18} />
                    </div>
                    <ToggleSwitch
                      checked={source.enabled}
                      onChange={() => handleSourceToggle(key)}
                      size="sm"
                      variant="bordered"
                    />
                  </div>
                  <h3 className="text-sm text-ivory font-medium">{source.label}</h3>
                  <p className="text-xs text-pearl/50 mt-1 leading-relaxed">{source.description}</p>
                </div>
              )
            })}
          </div>
        </motion.div>

        {/* Research & News */}
        <motion.div variants={itemVariants}>
          <div className="space-y-3">
            <SectionHeading className="font-medium">{t('researchNews')}</SectionHeading>
            <div className="glass-card rounded-xl p-4">
              <p className="text-sm text-pearl/60 leading-relaxed">
                {t('cortexResearchDescription')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Last Briefing */}
        {data.lastBriefing && (
          <motion.div variants={itemVariants}>
            <LastBriefingCard
              lastBriefing={data.lastBriefing}
              isExpanded={expand.isExpanded('lastBriefing')}
              onToggleExpand={() => expand.toggle('lastBriefing')}
            />
          </motion.div>
        )}
      </motion.div>
      </div>
    </motion.div>
  )
}
